import * as _ from "lodash";
import * as $ from "jquery";
import {BaseView} from "../_BaseViews";
import {DashboardWidgetCollection} from "../../collections/DashboardWidgets";
import {DashboardCategoryCollection} from "../../collections/DashboardCategories";
import {PermissionCollection} from "../../collections/Permissions";
import {DashboardChannelCollection} from "../../collections/DashboardChannels";
import {DashboardWidgetManager} from "./widgets/DashboardWidgetManager";
import {GenericDeleteModal} from "../modals/GenericDeleteModal";
import {GenericInputModal} from "../modals/GenericInputModal";
import {GenericTabSelectModal} from "../modals/GenericTabSelectModal";
import {DashboardTabCollection} from "../../collections/DashboardTabs";
import {BaseDashboardWidgetView} from "./widgets/_BaseWidgetView";
import {DashboardChannel} from "../../models/DashboardChannel";
import {BaseDashboardCategoryLayout} from "./_BaseDashboardCategoryLayout";
import {DashboardPackeryLayout} from "./DashboardPackeryLayout";

declare var App;

export interface CategoryViewOptions extends Backbone.ViewOptions<Backbone.Model> {
    permissionCollection: PermissionCollection;
    dashboardChannelCollection: DashboardChannelCollection;
    dashboardCategoryCollection: DashboardCategoryCollection;
    dashboardWidgetCollection: DashboardWidgetCollection;
    dashboardId: number;
    dashboardTabCollection?: DashboardTabCollection;
}

export interface DashboardCategoryViewOptions extends CategoryViewOptions {
    widgetManager: DashboardWidgetManager;
}

export class DashboardCategoryView extends BaseView {

    options: DashboardCategoryViewOptions;

    isLastVisibleCategory: boolean = true;
    isFirstVisibleCategory: boolean = true;
    categoryIndex: number = null;
    lastFilterValue: string = null;
    template = _.template(require('templates/dashboard/dashboardCategory.html'));
    widgetViews: BaseDashboardWidgetView[] = [];
    widgets: DashboardWidgetCollection = new DashboardWidgetCollection();
    layout: BaseDashboardCategoryLayout = null;

    private doingLayout = false;

    /**
     * Helper method to build a URL with a category ID, properly handling query strings
     */
    private buildCategoryUrl(categoryId: number, suffix: string = ''): string {
        var baseUrl = this.options.dashboardCategoryCollection.url();
        var queryIndex = baseUrl.indexOf('?');
        var url: string;
        
        if (queryIndex !== -1) {
            // Insert categoryId before the query string
            url = baseUrl.substring(0, queryIndex) + '/' + categoryId + suffix + baseUrl.substring(queryIndex);
        } else {
            // No query string, just append categoryId
            url = baseUrl + '/' + categoryId + suffix;
        }
        
        return url;
    }

    constructor(options: DashboardCategoryViewOptions) {
        super(<any> _.extend({
            tagName: "div",
            events: {
                'click .w-add-tile': 'handleAddWidgetClick',

                // Change Chart Types
                'click .dashboard-container-chart-type': 'handleChangeChartType',
                'click .dashboard-container-chart-period': 'handleChangeChartType',
                'click .dashboard-container-chart-stat': 'handleChangeChartType',
                'click .dashboard-container-chart-label': 'handleChangeChartType',
                'click .dashboard-container-chart-showthreshold': 'handleChangeChartType',
                'click .dashboard-container-chart-roundtimestamps': 'handleChangeChartType',

                // Edit Category
                'click .head .category-icons .clone': 'handleCategoryCloneClick',
                'click .head .category-icons .minus': 'handleCategoryCollapseClick',
                'click .head .category-icons .edit': 'handleCategoryEditClick',
                'click .head .category-icons .move-tab': 'handleCategoryMoveTabClick',
                'click .head .category-icons .delete': 'handleCategoryDeleteClick',
                'click .head .category-icons .add': 'handleCategoryAddClick',
                'click .head .category-icons .move-up': 'handleCategorySwap',
                'click .head .category-icons .move-down': 'handleCategorySwap',
            }
        }, options));

        var categoryId = this.model.get('id');
        this.options.dashboardCategoryCollection.each((category, idx) => {
            if (category.get('id') === categoryId) {
                this.categoryIndex = idx;
            } else if (this.categoryIndex === null) {
                // We are before the view's category in the list of categories
                if (!category.get('minimal')) this.isFirstVisibleCategory = false;
            } else {
                // We are after the view's category in the list of categories
                if (!category.get('minimal')) this.isLastVisibleCategory = false;
            }
        });

        this.listenTo(App.Bus, 'dashboard:category:' + this.model.get('id') + ':render', this.render);
        this.listenTo(App.Bus, 'dashboard:category:' + this.model.get('id') + ':layout', () => {
            this.doLayout();
        });

        this.listenTo(App.Bus, 'dashboard:filter:notify', (filtervalue) => {
            this.lastFilterValue = filtervalue;
        });
        this.renderMain = _.debounce(this.renderMain.bind(this), 500, {leading: true});
    }

    setLayout(layout?: BaseDashboardCategoryLayout) {
        this.layout = layout ? layout : new DashboardPackeryLayout({
            permissionCollection: this.options.permissionCollection,
            model: this.model,
            $el: this.$el,
            maxColumns: 6
        });

        this.doLayout();
    }

    private renderMain() {
        while (this.widgetViews.length) this.widgetViews.pop().remove();

        this.$el.html(this.template({
            permissionCollection: this.options.permissionCollection,
            dashboardCategoriesCollection: this.options.dashboardCategoryCollection,
            minimal: this.model.get('minimal'),
            categoryName: this.model.get('name'),
            categoryId: this.model.get('id'),
            dashboardId: this.model.get('dashboard_id'),
            showMoveUp: !this.isFirstVisibleCategory,
            showMoveDown: !this.isLastVisibleCategory,
            showDelete: !(this.isFirstVisibleCategory && this.isLastVisibleCategory),
            categoryIndex: this.categoryIndex
        }));
    }

    private doLayout() {
        if (this.doingLayout) return;
        this.doingLayout = true;

        return $.when(this.renderOrRemoveSubViews()).done((viewsAddedOrRemoved) => {
            if (this.layout && !this.layout.isInitialized() && this.$el.is(':visible')) {
                this.layout.initialize()
            } else if (this.layout) {
                this.layout.layout(viewsAddedOrRemoved);
            }
            this.doingLayout = false;
        });
    }

    private renderOrRemoveSubViews() {
        var deferred = $.Deferred();

        this.widgets = new DashboardWidgetCollection(this.options.dashboardWidgetCollection.where({
            dashboard_category_id: this.model.get('id')
        }));

        var widgetViewsRemoved = false;
        var widgetViewsAdded = false;

        // First Remove Views that are already rendered that no longer have a model in the collection
        // And keep track of widgets that are already rendered
        var widgetsSeen = [];
        for (var i = 0; i < this.widgetViews.length; i++) {
            var viewWidget = this.widgetViews[i].options.widget;
            if (!viewWidget) {
                this.widgetViews.splice(i, 1);
                widgetViewsRemoved = true;
                i--;
            } else if (viewWidget.get('dashboard_category_id') !== this.model.get('id')) {
                // A widget was moved to a different category
                this.widgetViews[i].remove();
                this.widgetViews.splice(i, 1);
                widgetViewsRemoved = true;
                i--;
            }

            this.widgets.each((widget) => {
                if (viewWidget == widget) {
                    widgetsSeen.push(widget);
                }
            }, this);
        }

        var newWidgetViewDeferreds = [];
        // Next Add widgets that are not yet rendered

        // The following is the default widget sort order. The order final layout is determined by packery
        // depending on whether the user has a saved widget layout
        var dashboardChannels = this.options.dashboardChannelCollection.where({
            dashboard_category_id: this.model.get('id')
        });

        // Second render simple widgets sorted by the number of channels each widget has
        // We're rendering simple widgets first, because it looks nicer if the user does not have a saved
        // category layout
        var defaultChannels = <DashboardChannel[]> _.filter(dashboardChannels, (c) => {
            return c.get('dashboard_widget_type') === 'channels'
        });
        var sortedChannels = (<any[][]> _.values(
            _.groupBy(defaultChannels, (c) => {
                return c.get('dashboard_widget_id')
            })
        )).sort((a: DashboardChannel[], b: DashboardChannel[]) => {
            if (a.length === b.length) {
                // If same length sort by order asc
                return Number(a[0].get('dashboard_widget_order') - b[0].get('dashboard_widget_order'));
            }

            // Sort by length desc
            return Number(b.length - a.length);
        });
        _.each(sortedChannels, (channels: DashboardChannel[]) => {
            var widget = this.widgets.findWhere({id: channels[0].get('dashboard_widget_id')});
            if (!widget || _.includes(widgetsSeen, widget)) return;

            var deferred = this.options.widgetManager.getViewFromWidgetModel(widget, {
                dashboardChannels: channels
            }, this);

            newWidgetViewDeferreds.push(deferred);
            widgetsSeen.push(widget);
            widgetViewsAdded = true;
        });

        // Third render all widgets that we haven't seen yet (i.e. all non-simple widgets)
        this.widgets.each((widget) => {
            if (!widget || _.includes(widgetsSeen, widget)) return;

            var deferred = this.options.widgetManager.getViewFromWidgetModel(widget, {}, this);
            newWidgetViewDeferreds.push(deferred);
            widgetViewsAdded = true;
        });

        $.when.apply($, newWidgetViewDeferreds).done((...args) => {
            var views = <BaseDashboardWidgetView[]> args;
            var container = this.$('.widgets');

            _.each(views, (widgetView) => {
                if (widgetView && widgetView.hasNeededPermissions()) {
                    this.widgetViews.push(widgetView);
                    container.append(widgetView.$el);
                    widgetView.render();
                }
            });

            deferred.resolve(widgetViewsAdded || widgetViewsRemoved);
        });

        return deferred;
    }

    render() {
        this.renderMain();
        return this;
    }

    remove() {
        // Remove listeners from the global event bus
        this.stopListening();
        if (this.layout) this.layout.remove();

        while (this.widgetViews.length) this.widgetViews.pop().remove();
        return super.remove();
    }

    private handleCategoryCloneClick(e) {
        var categoryId = Number($(e.currentTarget).parents('.category').attr('data-category-id'));
        var categoryModel = this.options.dashboardCategoryCollection.get(categoryId);
        var name = categoryModel ? categoryModel.get('name') : '';

        // An elaborate operation to provide an order 1 higher than the highest existing category order
        var dashboardCategoryOrders = [];
        this.options.dashboardCategoryCollection.each((model) => {
            dashboardCategoryOrders.push(model.get('order'));
        });
        var order = dashboardCategoryOrders.sort()[dashboardCategoryOrders.length - 2] + 1;
        
        // Get tabId from collection or from current category model
        var tabId = this.options.dashboardCategoryCollection.tabId;
        if (tabId === undefined && this.model.get('dashboard_tab_id')) {
            tabId = this.model.get('dashboard_tab_id');
        }

        var modal = new GenericInputModal({
            titleText: 'Clone Category ' + name,
            text: 'Select a name for your cloned Category',
            name: name,
            buttonText: 'Clone',
            func: (newName) => {
                var requestData: any = {
                    dashboard_category_id: categoryId,
                    name: newName,
                    order: order
                };
                
                // Include tabId if available
                if (tabId !== undefined && tabId !== null) {
                    requestData.tabId = tabId;
                }
                
                return $.ajax({
                    type: "POST",
                    url: this.buildCategoryUrl(categoryId, '/clone'),
                    data:  JSON.stringify(requestData),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8",
                    complete: (res) => {
                        this.reloadUrl();
                    }
                });
            }
        });
        $('.modals').append(modal.render().el);
        modal.show();
    }

        private handleCategoryEditClick(e: JQuery.UIEventBase) {
            var categoryId = Number($(e.currentTarget).parents('.category').attr('data-category-id'));
            var name = this.model.get('name');
            var self = this; // Preserve context

            var modal = new GenericInputModal({
                titleText: 'Edit Category ' + name,
            text: 'Choose a new name for this category:',
            name: name,
            buttonText: 'Update',
            func: (newName) => {
                // Ensure we have the correct URL
                var editUrl = self.buildCategoryUrl(categoryId);
                return $.ajax({
                    type: "PUT",
                    url: editUrl,
                    data:  JSON.stringify({
                        name: newName,
                        dashboardId: self.options.dashboardId,
                        order: null
                    }),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8",
                    complete: (res) => {
                        self.reloadUrl();
                    }
                });
            }
        });
        $('.modals').append(modal.render().el);
        modal.show();
    }

    private handleCategoryMoveTabClick(e: JQuery.UIEventBase) {
        var categoryId = Number($(e.currentTarget).parents('.category').attr('data-category-id'));
        var categoryName = this.model.get('name');
        var currentTabId = this.model.get('dashboard_tab_id');
        var self = this;

        // Check if dashboardTabCollection is available
        if (!this.options.dashboardTabCollection || this.options.dashboardTabCollection.length === 0) {
            // No tabs available, show error or return
            return;
        }

        var modal = new GenericTabSelectModal({
            titleText: 'Move Category ' + categoryName,
            text: 'Choose a new tab for this category:',
            buttonText: 'Move',
            dashboardTabCollection: this.options.dashboardTabCollection,
            currentTabId: currentTabId,
            func: (selectedTabId: number) => {
                // Fetch all categories (not filtered by tab) to find categories in the target tab
                var allCategoriesCollection = new DashboardCategoryCollection([], {
                    dashboardId: self.options.dashboardId
                    // No tabId - fetch all categories from all tabs
                });
                
                var deferred = $.Deferred();
                
                allCategoriesCollection.getCollectionFetchPromise().done(() => {
                    // Calculate the order for the new tab - should be last (max order + 1)
                    var targetTabCategories = allCategoriesCollection.filter((category) => {
                        var categoryTabId = category.get('dashboard_tab_id');
                        // Match categories in the target tab (including null if selectedTabId is null)
                        return (selectedTabId === null ? categoryTabId === null : categoryTabId === selectedTabId) && 
                               !category.get('minimal'); // Exclude minimal categories (like Graph category)
                    });
                    
                    var maxOrder = 0;
                    if (targetTabCategories.length > 0) {
                        var orders = _.map(targetTabCategories, (category) => category.get('order'));
                        maxOrder = _.max(orders) || 0;
                    }
                    
                    // Set order to be one more than the max order in the target tab
                    var newOrder = maxOrder + 1;
                    
                    // Ensure we have the correct URL
                    var editUrl = self.buildCategoryUrl(categoryId);
                    $.ajax({
                        type: "PUT",
                        url: editUrl,
                        data: JSON.stringify({
                            name: categoryName,
                            dashboardId: self.options.dashboardId,
                            order: newOrder,
                            tabId: selectedTabId
                        }),
                        dataType: 'json',
                        contentType: "application/json; charset=utf-8",
                        complete: (res) => {
                            self.reloadUrl();
                        }
                    }).done(() => {
                        deferred.resolve();
                    }).fail((err) => {
                        deferred.reject(err);
                    });
                });
                
                return deferred;
            }
        });
        $('.modals').append(modal.render().el);
        modal.show();
    }

    private handleCategoryCollapseClick(e: JQuery.UIEventBase) {
        var categoryId = Number($(e.currentTarget).parents('.category').attr('data-category-id'));

        setTimeout(() => {
            // Tell Packery to do its magic again so that our widgets are re-organized
            App.Bus.trigger('dashboard:category:' + categoryId + ':layout');
        }, 250);

        // Change icon to be plus or minus, depending on state of category
        $(e.currentTarget).toggleClass('fa-compress fa-expand');
    }

    private handleCategoryDeleteClick(e) {
        e.preventDefault();

        var id = this.model.get('id');
        var modal = new GenericDeleteModal({
            type: 'Dashboard Category',
            name: this.model.get('name'),
            func: () => {
                App.Bus.trigger('dashboard:setLoading');
                $.when($.ajax({
                    type: "DELETE",
                    url: this.buildCategoryUrl(id),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8"
                }).done(() => {
                    this.reloadUrl();
                }));
            }
        });
        $('.modals').append(modal.render().el);
        modal.show();
    }

    private handleCategoryAddClick(e) {
        var categoryId = Number($(e.currentTarget).parents('.category').attr('data-category-id'));
        
        // Get tabId from collection or from current category model
        var tabId = this.options.dashboardCategoryCollection.tabId;
        if (tabId === undefined && this.model.get('dashboard_tab_id')) {
            tabId = this.model.get('dashboard_tab_id');
        }

        var modal = new GenericInputModal({
            titleText: 'Add Category',
            text: 'Select a name for your new Category',
            name: null,
            buttonText: 'Add Category',
            func: (newName) => {
                var requestData: any = {
                    after: categoryId,
                    name: newName
                };
                
                // Include tabId if available
                if (tabId !== undefined && tabId !== null) {
                    requestData.tabId = tabId;
                }
                
                return $.ajax({
                    type: "POST",
                    url: this.options.dashboardCategoryCollection.url(),
                    data:  JSON.stringify(requestData),
                    dataType: 'json',
                    contentType: "application/json; charset=utf-8",
                    complete: (res) => {
                        this.reloadUrl();
                    }
                });
            }
        });
        $('.modals').append(modal.render().el);
        modal.show();
    }

    private handleCategorySwap(e: JQuery.UIEventBase) {
        // prevent form submit
        e.preventDefault();

        var id = this.model.get('id');
        var swapId = Number($(e.currentTarget).attr('data-swap-id'));

        App.Bus.trigger('dashboard:setLoading');
        $.when($.ajax({
            type: "PUT",
            url: this.buildCategoryUrl(id),
            dataType: 'json',
            data: JSON.stringify({swapId: swapId}),
            contentType: "application/json; charset=utf-8"
        }).done(() => {
            this.reloadUrl();
        }));
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/DashboardCategoryView.ts